import axios, { isAxiosError, Method } from "axios";
import { AuthenticationService } from "./authenticationService";
import { createURLSearchParams } from "src/helpers";
import { APIError } from "src/errors";

class APIService {
  private readonly API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  private readonly INTERNAL_API_BASE_URL = process.env.REACT_APP_INTERNAL_API_BASE_URL;
  private readonly authenticationService: AuthenticationService;
  protected readonly resourcePath: string;

  constructor(authenticationService: AuthenticationService, resourcePath: string) {
    this.authenticationService = authenticationService;
    this.resourcePath = resourcePath;
  }

  private getApiBaseUrl(idToken: string | undefined): string | undefined {
    if (idToken && this.INTERNAL_API_BASE_URL) {
      try {
        const tokenPayload = JSON.parse(atob(idToken.split(".")[1]));
        const isHealfEmail = typeof tokenPayload.email === "string" && tokenPayload.email.endsWith("@healf.com");
        if (isHealfEmail) {
          return this.INTERNAL_API_BASE_URL;
        }
      } catch (error) {
        console.error("Failed to parse ID token payload:", error);
      }
    }
    return this.API_BASE_URL;
  }

  protected async jwtRequest<T>(
    httpMethod: Method,
    endpoint: string = "",
    requestBody?: object,
    queryParams?: object,
    resourcePathOverride?: string
  ): Promise<T> {
    try {
      const queryStringParams = queryParams ? createURLSearchParams({ ...queryParams }) : undefined;
      const idToken = await this.authenticationService.getCurrentIdToken();
      const response = await axios({
        baseURL: this.getApiBaseUrl(idToken),
        url: `${resourcePathOverride || this.resourcePath}${endpoint}${queryStringParams ? "?" + queryStringParams : ""}`,
        method: httpMethod,
        headers: { Authorization: `Bearer ${idToken}` },
        data: requestBody
      });
      return response.data;
    } catch (error) {
      throw isAxiosError(error) ? APIError.fromAxiosError(error) : error;
    }
  }

  protected async publicRequest<T>(
    httpMethod: Method,
    endpoint: string = "",
    requestBody?: object,
    queryParams?: object
  ): Promise<T> {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const queryStringParams = queryParams ? createURLSearchParams({ ...queryParams }) : undefined;
      const response = await axios({
        baseURL: API_BASE_URL,
        url: `${this.resourcePath}${endpoint}${queryStringParams ? "?" + queryStringParams : ""}`,
        method: httpMethod,
        headers: { "Content-Type": "application/json" },
        data: requestBody
      });
      return response.data;
    } catch (error) {
      throw isAxiosError(error) ? APIError.fromAxiosError(error) : error;
    }
  }
}

export { APIService };
